import React, { useEffect, useState } from "react";
import SearchBar from "../../Snippets/TableCustomHooks/SearchBar";
import { Button1, Button3, Button4 } from "../../Snippets/Button";
import Loader from "../../Snippets/Loader";
import Table from "../../Snippets/TableCustomHooks/Table";
import Pagination from "../../Snippets/TableCustomHooks/Pagination";
import EmptyDataAlert from "../../Snippets/TableCustomHooks/EmptyDataAlert";
import { capitalizeFirstLetter } from "../Header/Header";
import RolesAction from "./RolesAction";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { fetchRole } from "../../../Services/RoleManagement/RoleData";
import {
  addRoleReducer,
  deleteRoleReducer,
  updateRoleReducer,
} from "../../../Redux-reducer/RoleManage";
import { showFailed } from "../../../Customs/SweetAlerts";

const RolesTable = () => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [operation, setOperation] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [userId, setUserId] = useState();
  const [roles, setRoles] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddUser = () => {
    setOperation("Add");
    setPreviewOpen(true);
  };

  const handleClose = () => {
    setPreviewOpen(false);
  };

  // Fetch roles
  const fetchData = async (currentPage = 1) => {
    setIsLoading(true);

    const data = await fetchRole(searchQuery, currentPage);

    if (data) {
      // Sort roles alphabetically
      const sortedRoles = data.docs.sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setRoles(sortedRoles);
      setShowDetails(true);

      setPagination({
        page: data.page,
        totalPages: data.totalPages,
        hasPrevPage: data.hasPrevPage,
        hasNextPage: data.hasNextPage,
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchData(page);
  }, []);

  const goToPrevPage = () => {
    const newPage = pagination.page - 1;
    setPage(newPage);
    fetchData(newPage);
  };

  const goToNextPage = () => {
    const newPage = pagination.page + 1;
    setPage(newPage);
    fetchData(newPage);
  };

  const handleEdit = (_id) => {
    setOperation("update");
    setUserId(_id);
    setPreviewOpen(true);
  };

  const handleUpdate = (values) => {
    setIsLoadingUpdate(true);

    dispatch(updateRoleReducer({ formData: values, userId }));

    handleClose();

    setTimeout(() => {
      fetchData();
      setIsLoadingUpdate(false);
    }, 500);
  };

  const handleDelete = (_id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1D4ED8",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteRoleReducer(_id));

        setTimeout(async () => {
          Swal.fire({
            title: "Deleted!",
            text: "Role has been deleted.",
            icon: "success",
          });

          await fetchData();
        }, 1000);
      }
    });
  };

  const handleSubmit = (values) => {
    try {
      dispatch(addRoleReducer(values));
      setPreviewOpen(false);

      setTimeout(async () => {
        await fetchData();
      }, 1500);
    } catch (error) {
      showFailed("Failed to add role.");
    }
  };

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "Role",
        accessor: "name",
        Cell: ({ value }) => (
          <div className="text-left font-medium">
            {capitalizeFirstLetter(value)}
          </div>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Button3
              label="Edit"
              onClick={() => handleEdit(row.original._id)}
            />

            <Button4
              label="Delete"
              onClick={() => handleDelete(row.original._id)}
            />
          </div>
        ),
      },
    ],
    []
  );

  const data = React.useMemo(() => roles, [roles]);

  return (
    <>
      <div className="flex flex-col md:ml-64 mt-24 md:mt-28 bg-gray-200 min-h-screen">

        <div className="w-full max-w-6xl mx-auto px-4">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-7">
            <h1 className="text-2xl font-semibold text-heading-clr">
              Role Management
            </h1>
          </div>

          {/* Search */}
          <SearchBar
            placeholder="Search role"
            searchValue={searchQuery}
            setSearchValue={setSearchQuery}
            handleSearch={handleSearch}
          />

          {/* Add Button */}
          <div className="mt-6">
            <Button1 onClick={handleAddUser} label="Add Role +" />
          </div>

          {/* Table */}
          <div className="mt-6">

            {isLoading ? (
              <div className="flex justify-center py-20">
                <Loader />
              </div>
            ) : showDetails ? (

              <>
                <div className="overflow-x-auto rounded-lg bg-white shadow">

                  <Table columns={columns} data={data} />

                </div>

                {pagination?.totalPages > 1 && (
                  <div className="mt-6 flex justify-center">
                    <Pagination
                      page={pagination.page}
                      totalPages={pagination.totalPages}
                      hasPrevPage={pagination.hasPrevPage}
                      hasNextPage={pagination.hasNextPage}
                      goToPrevPage={goToPrevPage}
                      goToNextPage={goToNextPage}
                    />
                  </div>
                )}

              </>

            ) : (
              <div className="py-20">
                <EmptyDataAlert title="No Role Found" />
              </div>
            )}

          </div>
        </div>
      </div>

      {isPreviewOpen && (
        <RolesAction
          isPreviewOpen={isPreviewOpen}
          onClose={() => setPreviewOpen(false)}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          handleUpdate={handleUpdate}
          isLoadingUpdate={isLoadingUpdate}
          userId={userId}
          operation={operation}
        />
      )}
    </>
  );
};

export default RolesTable;